import React, { useCallback } from 'react'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import Modal from 'LuxKit/Modal/Modal'
import BodyText from 'LuxKit/Typography/BodyText'
import moment from 'moment'
import FormatDateTime from 'components/utils/Formatters/FormatDateTime'
import Group from 'components/utils/Group'
import useOptimizelyExperiment from 'hooks/Optimizely/useOptimizelyExperiment'
import { OptimizelyExperiments } from 'constants/optimizely'
import { DMY_CASUAL_FORMAT } from 'constants/dateFormats'
import MessageBanner from 'LuxKit/Banners/MessageBanner'
import LineInfoCircleIcon from 'LuxKit/Icons/line/LineInfoCircleIcon'
import Heading from 'LuxKit/Typography/Heading'
import Step from 'LuxKit/Steppers/Step'
import TextButton from 'LuxKit/Button/TextButton'
import ContactUSP from '../ContactUSP'
import Container from 'components/utils/Sheet'

const ModalTitles: Record<string, string> = {
  bnbl: 'Buy now, choose dates later',
  bestPrice: 'Our best price guarantee',
  gifting: 'Gifting available',
}
interface Props {
  open: boolean
  onClose: () => void
  modalType?: 'bnbl' | 'bestPrice' | 'gifting'
  bookBy: moment.Moment
  travelUntil: moment.Moment
  onViewPackageOptions?: () => void
}

function USPModal(props: Props) {
  const { open, onClose, modalType = '', bookBy, travelUntil, onViewPackageOptions } = props

  const isBestPrice = modalType === 'bestPrice'
  const isWePriceMatchEnabled = useOptimizelyExperiment(OptimizelyExperiments.croWePriceMatchOfferPageTag, isBestPrice)
  const title = isBestPrice && isWePriceMatchEnabled ? 'We price match' : ModalTitles[modalType]

  const handleViewPackageOptions = useCallback(() => {
    onViewPackageOptions?.()
    onClose()
  }, [onViewPackageOptions, onClose])

  return (
    <Modal
      isOpen={open}
      onClose={onClose}
      title={title}
    >
      {modalType === 'bnbl' && <VerticalSpacer gap={16}>
        <BodyText variant="medium">
          Our offers are live for a limited time. Secure your dream escape at a great price! No dates yet? Reserve the price now and pick dates later.
        </BodyText>

        <Heading variant="heading5" style={{ marginTop: 24 }}> How it works</Heading>

        <Group direction="vertical" gap={8}>
          <Group direction="horizontal" gap={12} verticalAlign="center">
            <Step number={1} label="" state="inactive" />
            <Group direction="vertical" gap={0}>
              <BodyText variant="large" weight="bold">Secure your escape today</BodyText>
              <BodyText variant="medium">Purchase now to lock in the price.</BodyText>
            </Group>
          </Group>
          <VerticalSpacer gap={8} />
          <Group direction="horizontal" gap={12} verticalAlign="center">
            <Step number={2} label="" state="inactive" />
            <Group direction="vertical" gap={0}>
              <BodyText variant="large" weight="bold">Choose dates later</BodyText>
              <BodyText variant="medium">
                Pick dates anytime before <strong>{moment(bookBy).format(DMY_CASUAL_FORMAT)}</strong> in your account.
              </BodyText>
            </Group>
          </Group>
          <VerticalSpacer gap={8} />
          <Group direction="horizontal" gap={12} verticalAlign="center">
            <Step number={3} label="" state="inactive" />
            <Group direction="vertical" gap={0}>
              <BodyText variant="large" weight="bold">Manage your booking online</BodyText>
              <BodyText variant="medium">
                Make sure to travel by <strong>{moment(travelUntil).format(DMY_CASUAL_FORMAT)}</strong>.
              </BodyText>
            </Group>
          </Group>
        </Group>

        <MessageBanner kind="informational-primary" density="compact">
          <Group direction="horizontal" gap={8} verticalAlign="center">
            <LineInfoCircleIcon />
            <BodyText variant="medium">
              Subject to availability and surcharges (peak period or property-specific). Any surcharges will be shown before confirming your dates.
            </BodyText>
          </Group>
        </MessageBanner>

      </VerticalSpacer>}
      {modalType === 'bestPrice' && <VerticalSpacer gap={24}>
        <BodyText variant="medium">
          We negotiate the best prices so you don't have to. In the unlikely event that you find a better price for the same offer, we promise to match it.
        </BodyText>
        <Group gap={8} direction="vertical">
          <BodyText weight="bold" variant="medium">
            1. Compare the same offer
          </BodyText>
          <BodyText variant="medium">
            Make sure the offer you've found is for the same hotel with the same VIP inclusions as our package.
          </BodyText>
          <BodyText weight="bold" variant="medium">
            2. Record the better price
          </BodyText>
          <BodyText variant="medium">
            Take a website screenshot or a photo of a catalogue so you can send it to us.
          </BodyText>
          <BodyText weight="bold" variant="medium">
            3. We'll match the price
          </BodyText>
          <BodyText variant="medium">
            Once verified we'll match the price.
          </BodyText>
        </Group>
      </VerticalSpacer>}
      {modalType === 'gifting' && <VerticalSpacer gap={24}>
        <BodyText variant="medium">
          Buy a package and send as a gift to a special someone. They must book by <FormatDateTime value={bookBy} format="dmy-casual" /> and can travel until <FormatDateTime value={travelUntil} format="dmy-casual" />.
        </BodyText>
        <Group gap={8} direction="vertical">
          <BodyText weight="bold" variant="medium">
            1. Purchase online
          </BodyText>
          <BodyText variant="medium">
            Purchasing this as a gift will secure the offer for your recipient and give them the flexibility to choose travel dates at a later time. Dates must be within the book by and validity period, and are subject to availability and surcharges.
          </BodyText>
        </Group>
        <Group gap={8} direction="vertical">
          <BodyText weight="bold" variant="medium">
            2. Send or schedule your gift
          </BodyText>
          <BodyText variant="medium">
            Provide the recipient's details and schedule a date so we can email it to them on your behalf.
          </BodyText>
        </Group>
        <Group gap={8} direction="vertical">
          <BodyText weight="bold" variant="medium">
            3. The world is their oyster
          </BodyText>
          <BodyText variant="medium">
            Your recipient can manage and make changes to their booking according to our 7-day ‘change of mind’ guarantee after booking (if applicable – refer to individual offer for more information).
          </BodyText>
        </Group>
      </VerticalSpacer>}

      <Container style={{ marginTop: 24 }}>
        <ContactUSP title="Need help?" description="Our team are available 24 hours, 7 days" />
      </Container>

      {
        onViewPackageOptions && (
          <VerticalSpacer gap={24}>
            <Group direction="horizontal" horizontalAlign="end">
              <TextButton
                kind="primary"
                onClick={handleViewPackageOptions}
                fit="mobile-full-width"
                style={{
                  marginTop: 16,
                }}
              >
                View package options
              </TextButton>
            </Group>
          </VerticalSpacer>
        )
      }

    </Modal>
  )
}

export default USPModal
